import { upflowyStoreRefs, useUpflowyStore, useUpflowyStoreRefs } from '@upflowy/firestore';
import { AnalyticsSpanOp, ConsumerAnalyticsService, FlowStatus, logger } from '@upflowy/logger';
import { FlowAssetDoc, FlowVersionDoc, ThemeVersionDoc } from '@upflowy/types';
import { doc, DocumentReference } from 'firebase/firestore';
import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import FlowNotAvailable from '../components/FlowNotAvailable';
import Loading from '../components/Loading';
import { fetchDoc, fetchDocs } from '../helpers/fetch-documents';
import { useNetworkState } from '../hooks/useNetworkState';
import { useRedirect } from '../hooks/useRedirect';
import { useFlowConfig } from '../providers/flow-config-provider';
import { useNetworkErrorHandler } from '../providers/network-error-handler-provider';

export const VersionContext = React.createContext<{
  versionId: string | null;
  version: FlowVersionDoc | null | undefined;
  flowVersionAssets: FlowAssetDoc[];
  themeVersion?: ThemeVersionDoc;
  dependenciesLoaded: boolean;
  setDependenciesLoaded?: React.Dispatch<React.SetStateAction<boolean>>;
}>({
  versionId: null,
  version: null,
  flowVersionAssets: [],
  dependenciesLoaded: false,
});

export const useFlowVersion = () => useContext(VersionContext);

export const VersionProvider = ({ children }: { children: ReactNode }) => {
  const firestore = useUpflowyStore();
  const { flowId } = useFlowConfig();
  const { versionId }: Record<string, string> = useParams();
  const [loading, setLoading] = useState(true);
  const [version, setVersion] = useState<FlowVersionDoc | null>();
  const [assets, setAssets] = useState<FlowAssetDoc[]>([]);
  const [themeVersion, setThemeVersion] = useState<ThemeVersionDoc>();
  const [dependenciesLoaded, setDependenciesLoaded] = useState<boolean>(false);
  const [networkError, setNetworkError] = useState(false);
  const { retryCount, networkErrorHandler } = useNetworkErrorHandler();
  const { isOnline } = useNetworkState();
  const handleRedirect = useRedirect();

  const retrievedFlowVersion = useMemo(() => version?.id, [version]);
  const retrievedAssets = useMemo(() => assets?.length, [assets]);
  const retrievedThemeVersion = useMemo(() => themeVersion?.id, [themeVersion]);

  const getVersionAsync = useCallback(
    async (theFlows: ReturnType<typeof useUpflowyStoreRefs>['flows']) => {
      if (flowId && versionId) {
        try {
          const snapshot = await fetchDoc(theFlows().id(flowId).versions().id(versionId).ref);
          return snapshot.data() as FlowVersionDoc;
        } catch (error) {
          networkErrorHandler(error, () => setNetworkError(!retrievedFlowVersion));
        }
      }

      if (flowId) {
        try {
          const snapshot = await fetchDocs(theFlows().id(flowId).versions().published().ref);
          if (!snapshot.empty) {
            return snapshot.docs[0].data() as FlowVersionDoc;
          }
        } catch (error) {
          networkErrorHandler(error, () => setNetworkError(!retrievedFlowVersion));
        }
      }

      return undefined;
    },
    [flowId, networkErrorHandler, retrievedFlowVersion, versionId],
  );

  // const getFlowVersionAsync = useCallback(async () => {
  //   const { flows } = upflowyStoreRefs(firestore);

  //   const flowVersion = await getVersionAsync(flows);

  //   if (flowVersion) {
  //     setVersion(flowVersion);
  //     return;
  //   }

  //   setVersion(undefined);
  // }, [firestore, getVersionAsync]);

  // useEffect(() => {
  //   if (!retrievedFlowVersion) {
  //     getFlowVersionAsync();
  //   }
  // }, [getFlowVersionAsync, flowId, versionId, retrievedFlowVersion, retryCount]);

  useEffect(() => {
    const getAssets = async () => {
      const { flows } = upflowyStoreRefs(firestore);

      try {
        if (flowId) {
          const flowVersion = await getVersionAsync(flows);

          // If we've previously retrieved the flowVersion
          // then we can just use that
          if (!flowVersion && retrievedFlowVersion) {
            return;
          }

          if (!flowVersion) {
            setVersion(null);
            return;
          }

          setVersion(flowVersion);

          const flowVersionRef = flows().id(flowId).versions().id(flowVersion.id);
          const assetsSnapshot = await fetchDocs(flowVersionRef.assets().ref);

          if (!assetsSnapshot.empty) {
            const assetDocs = assetsSnapshot.docs.map((d) => d.data() as FlowAssetDoc);
            const uniqueAssets = [
              ...new Map(assetDocs.map((asset) => [asset.url, asset])).values(),
            ];
            setAssets(uniqueAssets);

            if (flowVersion?.meta?.themeVersionRef) {
              try {
                const tv = (
                  await fetchDoc(
                    doc(
                      firestore,
                      flowVersion?.meta?.themeVersionRef,
                    ) as DocumentReference<ThemeVersionDoc>,
                  )
                ).data() as ThemeVersionDoc;
                if (tv) {
                  setThemeVersion(tv);
                }
              } catch (error) {
                networkErrorHandler(error, () => setNetworkError(!retrievedThemeVersion));
              }
            }
          }
        }
      } catch (error) {
        networkErrorHandler(error, () => setNetworkError(!retrievedAssets));
      } finally {
        setLoading(false);
      }
    };

    getAssets();
  }, [
    firestore,
    flowId,
    networkErrorHandler,
    retrievedAssets,
    retrievedThemeVersion,
    retryCount,
    getVersionAsync,
    retrievedFlowVersion,
  ]);

  useEffect(() => {
    if ((loading === false && !version) || !isOnline) {
      logger.logException(new Error('Flow Loading complete with offline page'));
      ConsumerAnalyticsService.endTransaction(FlowStatus.UNAVAILABLE);
    } else if (loading === false && version) {
      ConsumerAnalyticsService.startNewSpan({
        op: AnalyticsSpanOp.FLOW_RENDERING,
        description: 'Render the flow',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, loading]);

  const value = {
    versionId,
    version,
    flowVersionAssets: assets,
    themeVersion,
    dependenciesLoaded,
    setDependenciesLoaded,
  };

  logger.setContext('VersionProvider', value);

  if (networkError && !window.location.pathname.startsWith('/offline')) {
    handleRedirect(`/offline?returnTo=${window.location.pathname}`);
  }

  return (
    <>
      {loading && <Loading />}
      {!loading && version && (
        <VersionContext.Provider value={value}>{children}</VersionContext.Provider>
      )}
      {!loading && !version && isOnline && <FlowNotAvailable />}
    </>
  );
};
